import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Fade,
  Divider,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
  List,
  ListItem
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Componente per mostrare un pannello con suggerimenti sull'utilizzo di Minerva
 * 
 * @param {Object} props
 * @param {boolean} props.open - Se il pannello è visibile
 * @param {Function} props.onClose - Funzione chiamata alla chiusura
 * @param {Array} props.suggerimenti - Lista dei suggerimenti (stringa o oggetto {titolo, testo})
 * @param {boolean} props.isMobile - Se in modalità mobile
 * @param {string} props.title - Titolo del pannello (opzionale)
 * @returns {JSX.Element}
 */
const SuggerimentiPanel = ({
  open,
  onClose,
  suggerimenti = [],
  isMobile: propIsMobile = false,
  title = 'Suggerimenti per l\'utilizzo di Minerva'
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = propIsMobile || isSmallScreen;

  if (!open) return null;

  // Trasforma i suggerimenti in un formato standard con titolo e testo
  const formatSuggerimenti = suggerimenti.map((sugg, index) => {
    if (typeof sugg === 'string') {
      // Se è una stringa, crea un oggetto con titolo generico e la stringa come testo
      return {
        titolo: `Suggerimento #${index + 1}`,
        testo: sugg
      };
    } else if (sugg && typeof sugg === 'object' && sugg.titolo && sugg.testo) {
      // Se è già un oggetto con titolo e testo, usalo direttamente
      return sugg;
    } else {
      // Fallback per formati non riconosciuti
      return {
        titolo: `Suggerimento #${index + 1}`,
        testo: String(sugg)
      };
    }
  });

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: { xs: '50%', md: '60%' }, // Centrare su mobile (xs), mantenere spostato a destra (60%) su desktop (md)
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '80%', md: '65%' },
        backgroundColor: '#FFFFFF',
        border: '1px solid #DF4634',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
        borderRadius: '1.875rem',
        padding: isMobile ? '1rem' : '2rem',
        zIndex: 3000,
        maxHeight: '90vh',
        overflowY: 'auto',
        transition: 'all 0.3s ease-in-out',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {/* HEADER */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem' 
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#DF4634' }}>
          {title}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* CONTENUTO PRINCIPALE - LISTA SUGGERIMENTI */}
      <List sx={{ 
        width: '100%', 
        padding: 0,
        maxHeight: '70vh',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '3px'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#ccc',
          borderRadius: '3px',
          '&:hover': {
            background: '#aaa'
          }
        }
      }}>
        {formatSuggerimenti.map((suggerimento, index) => (
          <Box key={index}>
            <ListItem 
              sx={{ 
                padding: '1rem',
                borderRadius: '1rem',
                marginBottom: '0.5rem',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #f0f0f0',
                transition: 'all 0.2s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(223, 70, 52, 0.05)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: '1rem',
                width: '100%'
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start', 
                  padding: '0.4rem',
                }}>
                  <LightbulbOutlinedIcon 
                    sx={{ 
                      color: '#DF4634',
                      fontSize: '1.5rem',
                      padding: '0.3rem',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(223, 70, 52, 0.1)'
                    }} 
                  />
                </Box>
                
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1
                }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      marginBottom: '0.3rem',
                      color: '#DF4634',
                      textAlign: 'left'
                    }}
                  >
                    {suggerimento.titolo}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#6B7280',
                      fontSize: '0.875rem',
                      lineHeight: '1.5',
                      textAlign: 'left'
                    }}
                  >
                    {suggerimento.testo}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
            {index < formatSuggerimenti.length - 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Divider 
                  sx={{ 
                    width: '98%', 
                    opacity: 0.6,
                    borderColor: 'rgba(223, 70, 52, 0.1)'
                  }} 
                />
              </Box>
            )}
          </Box>
        ))}
      </List>

      <Box sx={{ mt: 3, textAlign: 'right' }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: '#DF4634',
            color: '#FFFFFF',
            borderRadius: '1rem',
            textTransform: 'none',
            padding: '0.6rem 1.5rem',
            '&:hover': {
              backgroundColor: '#E57373',
            },
          }}
        >
          Chiudi suggerimenti
        </Button>
      </Box>
    </Box>
  );
};

export default SuggerimentiPanel; 